import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Eye, CheckCircle2, MessageSquare, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { ArticleView } from "@/components/ArticleView";

function FeedbackBox({ id, token, existing }) {
  const [name, setName] = useState(existing?.name || "");
  const [comment, setComment] = useState("");
  const [saved, setSaved] = useState(existing || null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const submit = async (approved) => {
    if (!name.trim()) { setMsg("Inserisci il tuo nome"); return; }
    if (!approved && !comment.trim()) { setMsg("Scrivi un commento oppure premi Approva"); return; }
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/preview/${id}/feedback`, { token, name: name.trim(), approved, comment: comment.trim() });
      setSaved(r.data.feedback);
      setComment("");
      setMsg(approved ? "Grazie! Articolo approvato." : "Grazie! Commento inviato.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Invio non riuscito, riprova.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="feedback-box" className="mt-12 rounded-2xl border border-[#2D3748] bg-[#121620] p-6">
      <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[#8B5CF6]" /> Il tuo parere sull'articolo
      </h3>
      {saved && (
        <div data-testid="feedback-current" className={`mt-4 rounded-xl border p-4 ${saved.approved ? "border-emerald-500/30 bg-emerald-500/10" : "border-[#2D3748] bg-[#0B0D12]"}`}>
          {saved.approved && <p className="text-sm font-semibold text-emerald-400 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Approvato da {saved.name}</p>}
          {saved.comment && <p className="text-sm text-[#cbd5e1] mt-1">"{saved.comment}"{!saved.approved ? <span className="text-[#64748B]"> — {saved.name}</span> : ""}</p>}
        </div>
      )}
      <div className="mt-4 space-y-3">
        <input
          data-testid="feedback-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Il tuo nome"
          className="w-full bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#8B5CF6]"
        />
        <textarea
          data-testid="feedback-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Lascia un commento o richiedi modifiche (opzionale se approvi)"
          rows={3}
          className="w-full bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#8B5CF6] resize-y"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="feedback-approve" onClick={() => submit(true)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Approva
          </button>
          <button data-testid="feedback-send" onClick={() => submit(false)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#a78bfa] hover:bg-[#8B5CF6]/20 text-sm font-semibold disabled:opacity-60">
            <MessageSquare className="w-4 h-4" /> Invia commento
          </button>
          {msg && <span data-testid="feedback-msg" className="text-xs text-[#94A3B8]">{msg}</span>}
        </div>
      </div>
    </div>
  );
}

export default function Preview() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(sessionStorage.getItem("sl_lang") || "IT");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/preview/${id}`, { params: { token } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 401 ? "Link di anteprima non valido o scaduto." : "Anteprima non trovata."));
  }, [id, token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0D12] flex flex-col items-center justify-center gap-3 text-center px-6">
        <p className="text-[#94A3B8]" data-testid="preview-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0B0D12] flex items-center justify-center text-[#64748B]">Caricamento anteprima…</div>;
  }

  return (
    <div className="relative">
      <div data-testid="preview-banner" className="sticky top-0 z-50 bg-amber-500 text-[#0B0D12] text-center text-xs font-bold py-1.5 flex items-center justify-center gap-1.5">
        <Eye className="w-3.5 h-3.5" /> ANTEPRIMA — questo contenuto non è ancora pubblicato
      </div>
      <ArticleView
        item={data.item}
        brand={data.brand}
        caseLabel={data.case_label}
        lang={lang}
        onLangChange={(l) => { setLang(l); sessionStorage.setItem("sl_lang", l); }}
        mode="preview"
        feedbackSlot={<FeedbackBox id={id} token={token} existing={data.item.client_feedback} />}
      />
    </div>
  );
}
